<template>
  <b-modal
    id="EditUsageDataModal"
    class="modal"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    @hidden="doCancel"
  >
    <template
      slot="modal-header"
      slot-scope="{ close }"
    >
      <h5>Edit Usage Data</h5>
      <button
        class="btn btn--close"
        @click="close()"
      >
        <font-awesome-icon
          :icon="['fal', 'times']"
          class="icon alt"
        />
      </button>
    </template>

    <form-component style="min-height: 340px;">
      <form
        novalidate="novalidate"
        @submit.prevent
      >
        <input-text
          id="reasonForUpdate"
          v-model="reasonForUpdate"
          v-validate="'required'"
          name="reasonForUpdate"
          data-vv-name="reasonForUpdate"
          data-vv-as="Reason For Update"
          label="Reason For Update"
          type="text"
          required
          :error="errors.first('reasonForUpdate')"
        />

        <input-text
          id="tailNumber"
          v-model="tailNumber"
          name="tailNumber"
          data-vv-name="tailNumber"
          data-vv-as="Tail Number"
          label="Tail Number"
          type="text"
        />

        <input-text
          id="recorderPartNumber"
          v-model="recorderPartNumber"
          name="recorderPartNumber"
          data-vv-name="recorderPartNumber"
          data-vv-as="Recorder Part Number"
          label="Recorder Part Number"
          type="text"
        />

        <input-text
          id="recorderSerialNumber"
          v-model="recorderSerialNumber"
          name="recorderSerialNumber"
          data-vv-name="recorderSerialNumber"
          data-vv-as="Recorder Serial Number"
          label="Recorder Serial Number"
          type="text"
        />

        <responsive-table>
          <thead class="inline-header">
            <tr>
              <th>Reasons for Download</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(reason, index) in reasonsForDownload"
              :key="index"
            >
              <td>
                <input-text
                  v-model="reasonsForDownload[index]"
                  data-vv-as="Download Reason"
                  label="Download Reason"
                  type="text"
                />
              </td>
              <td class="actions">
                <button
                  v-b-tooltip.hover
                  class="red-hover"
                  title="Delete"
                  type="button"
                  @click="deleteReason(index)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'trash']"
                    class="icon alt"
                  />
                </button>
              </td>
            </tr>
            <tr>
              <td>
                <form
                  @submit="submitNewDownloadReason(newDownloadReason)"
                  @submit.prevent
                >
                  <input-text
                    id="newDownloadReason"
                    v-model="newDownloadReason"
                    name="newDownloadReason"
                    data-vv-name="newDownloadReason"
                    data-vv-as="New Download Reason"
                    label="New Download Reason"
                    type="text"
                    @blur="handleLeavingNewDownloadReason()"
                  />
                </form>
              </td>
              <td>
                <button
                  class="btn btn--primary inline-button"
                  type="button"
                  :disabled="!(newDownloadReason.trim().length > 0)"
                  @click="submitNewDownloadReason(newDownloadReason)"
                >
                  Add
                </button>
              </td>
            </tr>
          </tbody>
        </responsive-table>

        <input-text
          id="purchaseOrder"
          v-model="purchaseOrder"
          name="purchaseOrder"
          data-vv-name="purchaseOrder"
          data-vv-as="Purchase Order"
          label="Purchase Order"
          type="text"
        />

        <input-text
          id="invoiceNumber"
          v-model="invoiceNumber"
          name="invoiceNumber"
          data-vv-name="invoiceNumber"
          data-vv-as="Invoice Number"
          label="Invoice Number"
          type="text"
        />
      </form>
    </form-component>

    <template
      slot="modal-footer"
    >
      <button
        class="btn"
        type="button"
        @click="doCancel()"
      >
        Cancel
      </button>
      <button
        class="btn btn--primary"
        type="button"
        :disabled="saving"
        @click="save()"
      >
        <font-awesome-icon
          v-if="saving"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Save</span>
      </button>
    </template>
  </b-modal>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import FormComponent from '@/components/FormComponent.vue';
import InputText from '@/components/inputs/InputText.vue';
import ResponsiveTable from '../ResponsiveTable.vue';

export default {
  name: 'EditUsageDataModal',
  components: {
    FormComponent,
    InputText,
    ResponsiveTable,
  },
  props: {
    doCancel: {
      type: Function,
    },
    doSave: {
      type: Function,
    },
  },
  data: () => ({
    saving: false,
    id: 0,
    tailNumber: '',
    recorderPartNumber: '',
    recorderSerialNumber: '',
    reasonsForDownload: [],
    purchaseOrder: '',
    invoiceNumber: '',
    newDownloadReason: '',
    reasonForUpdate: '',
  }),
  methods: {
    save() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.errors.clear();
          this.saving = true;
          this.doSave({
            id: this.id,
            tailNumber: this.tailNumber ? this.tailNumber.toLocaleUpperCase() : null,
            recorderPartNumber: this.recorderPartNumber,
            recorderSerialNumber: this.recorderSerialNumber,
            recorderTags: this.reasonsForDownload
              .filter(reason => reason.trim().length > 0)
              .map(reason => reason.trim()),
            purchaseOrder: this.purchaseOrder,
            invoiceNumber: this.invoiceNumber,
            reasonForUpdate: this.reasonForUpdate,
          }, () => {
            this.saving = false;
          });
        }
      });
    },
    setModal(usageRecord) {
      this.newDownloadReason = '';
      this.reasonForUpdate = '';
      this.saving = false;
      this.id = usageRecord.id;
      this.tailNumber = usageRecord.tailNumber;
      this.recorderPartNumber = usageRecord.recorderPartNumber;
      this.recorderSerialNumber = usageRecord.recorderSerialNumber;
      this.reasonsForDownload = usageRecord.recorderTags ? [...usageRecord.recorderTags] : [];
      this.purchaseOrder = usageRecord.purchaseOrder;
      this.invoiceNumber = usageRecord.invoiceNumber;
    },
    deleteReason(index) {
      this.reasonsForDownload.splice(index, 1);
    },
    submitNewDownloadReason(newReason) {
      this.reasonsForDownload = [...this.reasonsForDownload, newReason];
      this.newDownloadReason = '';
    },
    handleLeavingNewDownloadReason() {
      if (this.newDownloadReason.trim().length > 0) {
        this.submitNewDownloadReason(this.newDownloadReason);
      }
    },
  },
};
</script>

<style scoped lang="scss">

</style>
